<template>
  <ScheduleBDetailsModal
    v-if="detailsModalVisible"
    :product="detailsModalProduct"
    @closeModal="detailsModalVisible = false"
  />
  <CopyProductsModal
    v-if="copyProductsModalVisible"
    :plants="plants"
    :productIds="selectedProductIds"
    @closeModal="onCopyModalClosed"
  />
  <TerminationModal
    v-if="terminationModalVisible"
    :plants="plants"
    :productIds="selectedProductIds"
    @closeModal="onTerminationModalClosed"
  />
  <ProductApplicationModal
    v-if="productApplicationModalVisible"
    :plants="plants"
    @closeModal="productApplicationModalVisible = false"
  />

  <div class="schedule-b-table" data-cy="schedule-b-table">
    <div class="grid-control__container" data-cy="header-container">
      <div v-if="canModifyCarts" class="schedule-b-table__actions">
        <Button
          type="button"
          theme-color="primary"
          :disabled="!selectedProductIds.length"
          @click="onCopyClicked"
          data-cy="copy-products-button"
          >Add Selected Product(s)</Button
        >
        <Button
          type="button"
          theme-color="error"
          :disabled="!selectedProductIds.length"
          @click="onTerminationClicked"
          data-cy="terminate-products-button"
          >Terminate Selected Product(s)</Button
        >
        <span>OR</span>
        <Button
          type="button"
          theme-color="success"
          @click="onAddClicked"
          data-cy="add-products-button"
          >Enter New Product(s)</Button
        >
      </div>
      <div class="pull-right">
        <TableSearch
          class="schedule-b-search"
          :value="searchInput"
          placeholder="Search by Name or Brand or Plant"
          @searchSubmitted="onSearchSubmitted"
        />
        <div class="status-filter-menu">
          <DropDownList
            text="Filter By Status"
            :dataItems="statusFilterOptions"
            textField="text"
            dataItemKey="status"
            value-render="valueTemplate"
            size="small"
            :value="null"
            @change="
              (e) =>
                e?.value?.status ? setCertifiedFilter(e.value?.status) : ''
            "
          >
            <template v-slot:valueTemplate>
              <div class="grid-actions-menu__value">Filter By Status</div>
            </template>
          </DropDownList>
        </div>
        <GridActionsMenu
          :additionalActions="gridActions"
          @actionClicked="onActionClicked"
          data-cy="grid-actions"
        />
      </div>
    </div>
    <ScheduleBGrid
      :filter="filter"
      :products="products"
      :loading="productsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalProducts"
      :sort="sortDescriptors"
      ref="gridRef"
      :getPropValues="getPropValuesAsync"
      @productsChanged="onProductsChanged"
      @detailsClicked="onDetailsClicked"
      @selectionChanged="onSelectionChanged"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref, watch } from "vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import type { GridFilterChangeEvent } from "@progress/kendo-vue-grid";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ScheduleBGrid from "@/views/scheduleB/ScheduleBGrid.vue";
import type { IdAndName } from "@/Domain/IdAndName";
import {
  getJsonFilterString,
  valueFilterConvert,
  FilterValuesTake,
  boolYNConvert,
  boolYNConvertBack,
} from "@/shared/helpers/filtersHelper";
import type {
  GetProductDisplayScheduleBWithFilteringRequest,
  PlantWithSelection,
} from "@/api/scheduleB";
import type { ProductWithSelection } from "@/api/kosherCertificates";
import ScheduleBDetailsModal from "@/views/scheduleB/ScheduleBDetailsModal.vue";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import TerminationModal from "@/views/scheduleB/TerminationModal.vue";
import CopyProductsModal from "@/views/scheduleB/CopyProductsModal.vue";
import ProductApplicationModal from "@/views/scheduleB/ProductApplicationModal.vue";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import {
  convertBackToNumber,
  convertNumber,
} from "@/Domain/EnumGroupConverter";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import type { ItemSortChangedOptions } from "@/Domain/ItemChangedOptions";
import type { EnumLabelType } from "@/Domain/EnumLabelType";
import {
  getLabelTypeFromString,
  getLabelTypeString,
} from "@/shared/helpers/enumHelper";
import { EnumDpm } from "@/Domain/EnumDpm";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridMenuActionType } from "../../Domain/GridMenuActionType";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import type { GridMenuAction } from "../../Domain/GridMenuAction";
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import enumHelper from "@/shared/helpers/enumHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const props = defineProps<{
  plants: IdAndName[];
  statusList: string[];
  canModifyCarts: boolean;
}>();

const defaultTake = 100;

const products = ref<ProductWithSelection[]>([]);
const selectedProductIds = ref<number[]>([]);
const totalProducts = ref<number>(0);
const productsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(defaultTake);
const searchInput = ref<string>("");
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const detailsModalVisible = ref<boolean>(false);
const terminationModalVisible = ref<boolean>(false);
const copyProductsModalVisible = ref<boolean>(false);
const productApplicationModalVisible = ref<boolean>(false);
const detailsModalProduct = ref<ProductWithSelection>(
  {} as ProductWithSelection
);

const gridActions = ref<GridMenuAction[]>([
  {
    action: GridMenuActionType.ShowCertifiedOnly,
    text: "Show Certified Only",
  },
]);

const gridRef = ref<InstanceType<typeof ScheduleBGrid> | null>(null);
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const tableName = "ScheduleB";

const statusFilterOptions = ref([
  { status: EnumMerchStatus.Certified, text: "Certified" },
  { status: EnumMerchStatus.Pending, text: "Pending" },
  { status: EnumMerchStatus.WebApp, text: "WebApp" },
  { status: EnumMerchStatus.Terminated, text: "Terminated" },
  { status: EnumMerchStatus.Terminated, text: "Withdrawn" },
]);

const getCertifiedFilter = (
  merchStatus: EnumMerchStatus = EnumMerchStatus.Certified
) => {
  return {
    logic: "and",
    filters: [
      {
        filters: [
          {
            field: "_Status",
            operator: "eq",
            value: EnumMerchStatus[merchStatus],
          },
        ],
        logic: "or",
      } as CompositeFilterDescriptor,
    ],
  } as CompositeFilterDescriptor;
};

const filter = ref<CompositeFilterDescriptor>(getCertifiedFilter());

const valueFiltersConvertOptions = [
  {
    column: "_Dpm",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumDpm[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumDpm, value);
      return EnumDpm[key as keyof typeof EnumDpm];
    },
  },
  {
    column: "_Group",
    convert: (value: number) => {
      return convertNumber(value);
    },
    convertBack: (value: string) => {
      return convertBackToNumber(value);
    },
  },
  {
    column: "_LabelType",
    convert: (value: number) => {
      return getLabelTypeString(value as EnumLabelType);
    },
    convertBack: (value: string) => {
      return getLabelTypeFromString(value);
    },
  },
  {
    column: "_Status",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumMerchStatus[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumMerchStatus, value);
      return EnumMerchStatus[key as keyof typeof EnumMerchStatus];
    },
  },
  {
    column: "Dist",
    convert: boolYNConvert,
    convertBack: boolYNConvertBack,
  },
  {
    column: "_Pesach",
    convert: boolYNConvert,
    convertBack: boolYNConvertBack,
  },
  {
    column: "SpecialRequirementsExists",
    convert: boolYNConvert,
    convertBack: boolYNConvertBack,
  },
] as ValueFieldConvertOption[];

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getProductDisplayScheduleBWithFiltering = async function (
  valuesForFieldOnly?: string,
  ignoreFilters?: boolean // Used for status filter menu
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value,
    plantList: props.plants.map((p) => p.ID).join(";"),
    statusList: props.statusList.join(";"),
    allPlantCompanies: false,
    includeConfidential: true,
    filters: !ignoreFilters
      ? getJsonFilterString(filter.value, valueFiltersConvertOptions)
      : "",
    take: take.value,
    skip: skip.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
  } as GetProductDisplayScheduleBWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetProductDisplayScheduleBWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, "GetProductDisplayScheduleBWithFiltering"),
    () => api.get("GetProductDisplayScheduleBWithFiltering", requestParams)
  );
};

const getProductsAsync = async function () {
  if (productsLoading.value) return;
  productsLoading.value = true;

  await handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering(),
    (data) => {
      products.value = data.ListItems;
      totalProducts.value = data.TotalItemsInList;
    },
    "ScheduleBTable.vue",
    "getProductsAsync"
  );

  productsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: ProductWithSelection[] = [];
  await handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "ScheduleBTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const clearSelection = function () {
  selectedProductIds.value = [];
  products.value.forEach((p) => (p.selected = false));
};

const resetPlantSelection = function () {
  (props.plants as PlantWithSelection[]).forEach((p) => (p.selected = false));
};

const onProductsChanged = function (
  skipValue: number,
  takeValue: number,
  filterEvent: GridFilterChangeEvent | null
) {
  if (filterEvent) {
    filter.value.filters = filterEvent.filter.filters;
  }
  if (take.value != takeValue) {
    take.value = takeValue;
    GridOptionsService.savePageSizeAsync(tableName, takeValue);
  }
  skip.value = skipValue;
  getProductsAsync();
};

const onDetailsClicked = function (product: ProductWithSelection) {
  detailsModalProduct.value = product;
  detailsModalVisible.value = true;
};

const onCopyModalClosed = function (cartCount: number) {
  copyProductsModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const onTerminationModalClosed = function (cartCount: number) {
  terminationModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const onCopyClicked = function () {
  resetPlantSelection();
  copyProductsModalVisible.value = true;
};

const onTerminationClicked = function () {
  terminationModalVisible.value = true;
};

const onAddClicked = function () {
  productApplicationModalVisible.value = true;
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  getProductsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getProductsAsync();
};

const onSelectionChanged = function (changedProducts: ProductWithSelection[]) {
  changedProducts.forEach((product) => {
    const index = selectedProductIds.value.findIndex(
      (p) => p === product.ProducedInOneId
    );
    if (product.selected) {
      if (index === -1) selectedProductIds.value.push(product.ProducedInOneId);
      else
        console.debug(
          `ScheduleBTable.vue: onSelectionChanged(): product already selected`,
          product,
          selectedProductIds.value[index]
        );
    } else if (index > -1) {
      selectedProductIds.value.splice(index, 1);
    } else {
      // If product is deselected and it is not
      // in the selectedProducts array - do nothing
    }
  });
};

const clearFilters = function () {
  console.debug("ScheduleBTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  searchInput.value = "";
  sortField.value = "";
  sortAscending.value = false;
  getProductsAsync();
};

const setCertifiedFilter = function (
  merchStatus: EnumMerchStatus = EnumMerchStatus.Certified
) {
  console.debug("ScheduleBTable.vue, setCertifiedFilters()");
  filter.value = getCertifiedFilter(merchStatus);

  searchInput.value = "";
  sortField.value = "";
  sortAscending.value = false;
  getProductsAsync();
};

const resetPagination = function () {
  if (take.value === defaultTake) return;

  skip.value = 0;
  take.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  getProductsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const onActionClicked = function (actionType: GridMenuActionType) {
  console.debug("ScheduleBTable, onActionClicked", actionType);
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ShowCertifiedOnly:
      setCertifiedFilter();
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

const updateStatusFilterOptionsAsync = async function () {
  return handleResponsePromiseAsync(
    getProductDisplayScheduleBWithFiltering("_Status", true),
    (data) => {
      statusFilterOptions.value = [];
      for (const status of data.ListItems) {
        statusFilterOptions.value.push({
          status: status._Status!,
          text: EnumMerchStatus[status._Status!],
        });
      }
    },
    "ScheduleBTable.vue",
    "getPropValuesAsync"
  );
};

watch(
  () => [props.plants],
  async () => {
    await getProductsAsync();
  }
);

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  getProductsAsync();
  updateStatusFilterOptionsAsync();
});
</script>

<style lang="scss">
.schedule-b-table {
  .grid-control__container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;

    .schedule-b-search {
      width: 400px;
    }
  }
}
</style>
